import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { api, type ImagePull } from "@/lib/api";

function groupByImage(pulls: ImagePull[]) {
  const groups = new Map<string, ImagePull[]>();
  for (const pull of pulls) {
    const list = groups.get(pull.image_name) ?? [];
    list.push(pull);
    groups.set(pull.image_name, list);
  }
  return [...groups.entries()]
    .map(([image_name, tags]) => ({
      image_name,
      tags: tags.sort((a, b) => b.pull_count - a.pull_count),
      totalPulls: tags.reduce((sum, t) => sum + t.pull_count, 0),
    }))
    .sort((a, b) => b.totalPulls - a.totalPulls);
}

export function ImagePulls() {
  const { data: pulls, isLoading } = useQuery({
    queryKey: ["image-pulls"],
    queryFn: api.listImagePulls,
  });

  const images = useMemo(() => groupByImage(pulls ?? []), [pulls]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold">Image pulls</h1>
        <p className="max-w-2xl text-sm text-muted-foreground">
          How many times each image:tag has been requested through the registry proxy, most
          pulled first. Counts requests, not bytes transferred — a machine that already has an
          image cached still re-fetches its manifest on every apply, so this measures "how
          often is this asked for," not "how much data actually moved."
        </p>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {!isLoading && images.length === 0 && (
        <p className="text-sm text-muted-foreground">No pulls recorded yet.</p>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {images.map((image) => (
          <Card key={image.image_name} className="flex flex-col">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-baseline justify-between gap-2 text-sm">
                <span className="truncate font-mono" title={image.image_name}>
                  {image.image_name}
                </span>
                <span className="shrink-0 font-sans text-xs font-normal text-muted-foreground">
                  {image.totalPulls} pull{image.totalPulls === 1 ? "" : "s"}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
                {image.tags.map((pull) => (
                  <div
                    key={pull.tag}
                    className="rounded-md border bg-muted/30 px-3 py-2 text-sm"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate font-mono text-xs" title={pull.tag}>
                        {pull.tag}
                      </span>
                      <span className="shrink-0 font-medium">{pull.pull_count}</span>
                    </div>
                    <div className="mt-1 flex justify-between gap-2 text-xs text-muted-foreground">
                      <span>First {new Date(pull.first_pulled_at).toLocaleDateString()}</span>
                      <span>Last {new Date(pull.last_pulled_at).toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
